import {
  Autocomplete,
  Badge,
  ActionIcon,
  Group,
  rem,
  OptionsFilter,
  ComboboxItem,
  Image,
  useMantineColorScheme,
  Tooltip,
  Button,
} from '@mantine/core';
import { useDebouncedState, useHotkeys } from '@mantine/hooks';
import {
  IconSearch,
  IconListSearch,
  IconMessageCircle,
  IconLayoutSidebarLeftCollapse,
  IconLayoutSidebarLeftExpand,
} from '@tabler/icons-react';
import classes from './HeaderSearch.module.css';
import { ActionToggle } from './ColorSchemeToggle/ColorSchemeToggle';
import React, { useState, useEffect, useRef } from 'react';
import SearchToggle from './AdvancedSearchToggle';
import SearchHighlightToggle from './SearchHighlightToggle';
import { loadDictionaryKeys } from '@/utils/dictionaryKeys';



const removeDiacritics = (str: string | null) => {
  if (str === null) {
    return '';
  }
  return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
};

const optionsFilter: OptionsFilter = ({ options, search }) => {
  const normalizedSearch = removeDiacritics(search.toLowerCase().trim());
  return (options as ComboboxItem[]).filter((option) => {
    const normalizedValue = removeDiacritics(option.value.toLowerCase().trim());
    return normalizedValue.includes(normalizedSearch);
  });
};

const links = [
  { link: '/docs/', label: 'Documentation' },
  { link: '/docs/about', label: 'About' },
];

export function HeaderSearch({
  onToggleNavbar,
  onSearch,
  isNavbarVisible,
  isMobile,
  handleAdvancedSearch,
  onToggleInPageSearch,
  bookTitle,
}: {
  onSearch: (query: string) => void;
  onToggleNavbar: () => void;
  isNavbarVisible: boolean;
  isMobile: boolean | undefined;
  handleAdvancedSearch: {
    open: () => void;
    close: () => void;
    toggle: () => void;
  };
  onToggleInPageSearch?: () => void;
  bookTitle?: string | null;
}) {
  const [entries, setEntries] = useState<string[]>([]);
  const [filteredData, setFilteredData] = useState<string[]>([]);
  const [value, setValue] = useDebouncedState('', 600);
  const { colorScheme } = useMantineColorScheme();
  const [decomposedWordList, setDecomposedWordList] = useState<{ value: string; label: string }[]>(
    []
  );

  const searchInputRef = useRef<HTMLInputElement>(null);

  // Set up the hotkey for focusing the search
  useHotkeys([
    [
      'mod+k',
      (event) => {
        event.preventDefault();
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      },
    ],
  ]);

  useEffect(() => {
    // Fetch the JSON data
    loadDictionaryKeys()
      .then((data) => {
        setEntries(data);
        const no_diacritics = data.map((entry: string) => ({
          value: removeDiacritics(entry),
          label: entry,
        }));
        setDecomposedWordList(no_diacritics);

        // console.log('First 10 entries:', data.slice(0, 10));
      })
      .catch((error) => console.error('Error fetching JSON data:', error));
  }, []);

  const items = links.map((link) => (
    <a
      key={link.label}
      href={link.link}
      className={classes.link}
      // onClick={(event) => event.preventDefault()}
    >
      {link.label}
    </a>
  ));

  useEffect(() => {
    onSearch(value);
  }, [value, onSearch]);

  const inputRef = useRef<HTMLInputElement>(null);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (
      (event.key === 'Enter' && isNavbarVisible && isMobile) ||
      (event.keyCode === 13 && isNavbarVisible && isMobile)
    ) {
      setTimeout(() => {
        onToggleNavbar();
        if (inputRef.current) {
          inputRef.current.blur(); // Blur the input field
        }
      }, 400); // Adjust the delay time (in milliseconds) as needed
    }
  };

  const selectEntries = (value: string) => {
    let selectedEntries = decomposedWordList.filter((entry) => entry.value === value);

    // If no entries are found and value contains "sh", replace "sh" with "s" and filter again
    if (selectedEntries.length === 0 && value.includes('sh')) {
      const modifiedValue = value.replace(/sh/g, 's');
      selectedEntries = decomposedWordList.filter((entry) => entry.value === modifiedValue);
    }

    const originalValue =
      selectedEntries.length > 0 ? selectedEntries.map((entry) => entry.label).join(' | ') : value;
    setValue(originalValue); // Directly update the parent state with the concatenated original values
    // console.log('Selected nondecomposedquery:', originalValue); // Debugging statement
  };

  return (
    <header className={classes.header}>
      <div className={classes.inner}>
        <Group gap={isMobile ? 'xs' : 'md'}>
          <Tooltip
            label={`${isNavbarVisible ? 'Hide panel' : 'Show panel'} (Ctrl+B)`}
            position="bottom"
          >
            <ActionIcon
              className={classes.navbarToggle}
              onClick={onToggleNavbar}
              variant="default"
              size={isMobile ? 'md' : 'lg'}
              aria-label={isNavbarVisible ? 'Hide panel' : 'Show panel'}
            >
              {isNavbarVisible ? (
                <IconLayoutSidebarLeftCollapse
                  className={classes.navbarToggleIcon}
                  stroke={1.5}
                />
              ) : (
                <IconLayoutSidebarLeftExpand className={classes.navbarToggleIcon} stroke={1.5} />
              )}
            </ActionIcon>
          </Tooltip>

          <ActionToggle />
          <SearchToggle handleAdvancedSearch={handleAdvancedSearch} isMobile={isMobile} />
          {bookTitle && !isMobile && (
            <SearchHighlightToggle
              isMobile={isMobile}
              onToggle={onToggleInPageSearch}
            />
          )}
        </Group>

        <Group preventGrowOverflow={false} wrap="nowrap" className={classes.groupContainer}>
          <Autocomplete
            className={classes.search}
            rightSection={
              isMobile ? (
                ''
              ) : (
                <Badge
                  className={classes.searchShortcut}
                  variant="outline"
                  size="xs"
                  style={{
                    marginLeft: '8px', // space outside the badge
                    textTransform: 'none', // disable uppercase
                    paddingRight: '8px',
                  }} // Add right padding
                >
                  Ctrl+K
                </Badge>
              )
            }
            rightSectionWidth={80}
            placeholder="Dictionary Search"
            leftSection={<IconSearch style={{ width: rem(16), height: rem(16) }} stroke={1.5} />}
            data={entries}
            onChange={selectEntries}
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
            limit={50}
            withScrollArea={true}
            maxDropdownHeight={200}
            onKeyDown={handleKeyDown} // Add the onKeyDown event handler
            ref={searchInputRef}
          />
        </Group>
      </div>
    </header>
  );
}
